import { createContext, forwardRef, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { type Control, type FieldPath, type FieldValues, type UseFormReturn } from "react-hook-form";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { maskSecret } from "@/utils/format";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { ExpandableTextarea } from "@/components/ExpandableTextarea";
import {
  RichTextEditor,
  type InlineImagePreviewer,
  type InlineImageUploader,
} from "@/components/RichTextEditor";
import { DatePicker } from "@/components/DatePicker";
import {
  AsyncSearchableSelect,
  SearchableSelect,
  SEARCHABLE_MIN_OPTIONS,
  type AsyncResolveFn,
  type AsyncSearchFn,
  type SearchableOption,
} from "@/components/SearchableSelect";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select as UISelect,
  SelectContent,
  SelectItem,
  SelectEmpty,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

export const REQ_CLS = "border-amber-300 bg-amber-50/70";

/**
 * Router-safe autofocus for a RHF field. Unlike the `autoFocus` attribute (fired
 * once at commit and easily lost to StrictMode double-mount or focus stolen by
 * sibling/layout effects after an SPA route change), this focuses in an effect —
 * after commit and after any synchronous mount focus — so it lands reliably.
 */
export function useAutoFocus<T extends FieldValues>(
  form: UseFormReturn<T>,
  name: FieldPath<T>,
  enabled = true,
) {
  useEffect(() => {
    if (enabled) form.setFocus(name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}

type FieldSize = "sm" | "lg";
const FieldSizeContext = createContext<FieldSize>("sm");

export function FormFieldSize({ value, children }: { value: FieldSize; children: ReactNode }) {
  return <FieldSizeContext.Provider value={value}>{children}</FieldSizeContext.Provider>;
}

const useFieldSize = () => useContext(FieldSizeContext);
const inputSizeCls = (size: FieldSize) => (size === "lg" ? "h-11 text-base" : "");

const RequiredCueContext = createContext<boolean>(false);

export function FormRequiredCue({ value = true, children }: { value?: boolean; children: ReactNode }) {
  return <RequiredCueContext.Provider value={value}>{children}</RequiredCueContext.Provider>;
}

const useRequiredCue = () => useContext(RequiredCueContext);

const reqCueCls = (required: boolean | undefined, cue: boolean) => (required && cue ? REQ_CLS : undefined);

function reqLabel(label: string, required?: boolean) {
  return (
    <FormLabel>
      {label}{required && <span className="text-destructive"> *</span>}
    </FormLabel>
  );
}

export function TextField<T extends FieldValues>({
  control, name, label, required, placeholder, inputMode, maxLength, type = "text", hint, className, autoComplete, autoFocus, format, min, max,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  required?: boolean;
  placeholder?: string;
  inputMode?: "text" | "email" | "tel" | "numeric" | "url";
  maxLength?: number;
  type?: string;
  hint?: string;
  className?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  format?: (v: string) => string;
  min?: number;
  max?: number;
}) {
  const size = useFieldSize();
  const cue = useRequiredCue();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {reqLabel(label, required)}
          <FormControl>
            <Input
              type={type}
              inputMode={inputMode}
              maxLength={maxLength}
              min={min}
              max={max}
              placeholder={placeholder}
              autoComplete={autoComplete}
              autoFocus={autoFocus}
              className={cn(inputSizeCls(size), reqCueCls(required, cue))}
              name={field.name}
              ref={field.ref}
              onBlur={field.onBlur}
              value={typeof field.value === "string" ? field.value : ""}
              onChange={(e) => {
                const v = format ? format(e.target.value) : e.target.value;
                if (v !== "") {
                  const n = Number(v);
                  if (min !== undefined && n < min) return;
                  if (max !== undefined && n > max) return;
                }
                field.onChange(v);
              }}
            />
          </FormControl>
          {hint && <FormDescription>{hint}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function PasswordField<T extends FieldValues>({
  control, name, label, required, autoComplete, hint, className, autoFocus,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  required?: boolean;
  autoComplete?: string;
  hint?: string;
  className?: string;
  autoFocus?: boolean;
}) {
  const size = useFieldSize();
  const cue = useRequiredCue();
  const [show, setShow] = useState(false);
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {reqLabel(label, required)}
          <div className="relative">
            <FormControl>
              <Input
                type={show ? "text" : "password"}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                spellCheck={false}
                className={cn(inputSizeCls(size), reqCueCls(required, cue), "pr-10")}
                name={field.name}
                ref={field.ref}
                onBlur={field.onBlur}
                value={typeof field.value === "string" ? field.value : ""}
                onChange={(e) => field.onChange(e.target.value)}
              />
            </FormControl>
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShow((v) => !v)}
              aria-label={`${show ? "Hide" : "Show"} ${label.toLowerCase()}`}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {hint && <FormDescription>{hint}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function MaskedField<T extends FieldValues>({
  control, name, label, required, hint, className, placeholder, inputMode = "numeric", format,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  required?: boolean;
  hint?: string;
  className?: string;
  placeholder?: string;
  inputMode?: "text" | "email" | "tel" | "numeric" | "url";
  format?: (v: string) => string;
}) {
  const size = useFieldSize();
  const cue = useRequiredCue();
  const [editing, setEditing] = useState(false);
  const editingRef = useRef(false);
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const raw = typeof field.value === "string" ? field.value : "";
        const showRaw = editing || editingRef.current;
        return (
          <FormItem className={className}>
            {reqLabel(label, required)}
            <FormControl>
              <Input
                type="text"
                inputMode={inputMode}
                placeholder={placeholder}
                autoComplete="off"
                spellCheck={false}
                className={cn(inputSizeCls(size), reqCueCls(required, cue))}
                name={field.name}
                ref={field.ref}
                value={showRaw ? raw : maskSecret(raw)}
                onFocus={(e) => e.currentTarget.select()}
                onBlur={() => { editingRef.current = false; setEditing(false); field.onBlur(); }}
                onChange={(e) => {
                  editingRef.current = true;
                  setEditing(true);
                  field.onChange(format ? format(e.target.value) : e.target.value);
                }}
              />
            </FormControl>
            {hint && <FormDescription>{hint}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

export function TextAreaField<T extends FieldValues>({
  control, name, label, placeholder, hint, className, rows = 3, maxLength, autoGrow = false,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  placeholder?: string;
  hint?: string;
  className?: string;
  rows?: number;
  maxLength?: number;
  autoGrow?: boolean;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const value = typeof field.value === "string" ? field.value : "";
        const shared = {
          maxLength,
          placeholder,
          name: field.name,
          ref: field.ref,
          onBlur: field.onBlur,
          value,
          onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => field.onChange(e.target.value),
        };
        return (
          <FormItem className={className}>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              {autoGrow ? (
                <ExpandableTextarea minRows={rows} {...shared} />
              ) : (
                <Textarea rows={rows} {...shared} />
              )}
            </FormControl>
            {hint && <FormDescription>{hint}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

export function RichTextField<T extends FieldValues>({
  control, name, label, required, placeholder, hint, className, compact = false, above, uploadImage,
  previewImage,
  revealEndSignal,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  required?: boolean;
  placeholder?: string;
  hint?: string;
  className?: string;
  compact?: boolean;
  above?: ReactNode;
  uploadImage?: InlineImageUploader;
  previewImage?: InlineImagePreviewer;
  revealEndSignal?: number;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          {reqLabel(label, required)}
          {above}
          <FormControl>
            <RichTextEditor
              value={typeof field.value === "string" ? field.value : ""}
              onChange={field.onChange}
              placeholder={placeholder}
              compact={compact}
              invalid={fieldState.invalid}
              uploadImage={uploadImage}
              previewImage={previewImage}
              revealEndSignal={revealEndSignal}
            />
          </FormControl>
          {hint && <FormDescription>{hint}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function SelectField<T extends FieldValues>({
  control, name, label, required, placeholder, options, hint, className, disabled, searchable, searchPlaceholder, emptyText,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  required?: boolean;
  placeholder?: string;
  options: readonly (readonly [string, ReactNode, ReactNode?])[];
  hint?: string;
  className?: string;
  disabled?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
}) {
  const size = useFieldSize();
  const cue = useRequiredCue();
  const textOnly = options.every(([, l, d]) => typeof l === "string" && (d == null || typeof d === "string"));
  const withSearch = textOnly && (searchable ?? options.length >= SEARCHABLE_MIN_OPTIONS);

  if (withSearch) {
    return (
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem className={className}>
            {reqLabel(label, required)}
            <FormControl>
              <SearchableSelect
                ref={field.ref}
                value={typeof field.value === "string" ? field.value : ""}
                onValueChange={field.onChange}
                options={options.map(([v, l, d]) => ({ value: v, label: l as string, hint: typeof d === "string" ? d : undefined }))}
                placeholder={placeholder ?? "Select…"}
                searchPlaceholder={searchPlaceholder ?? `Search ${label.toLowerCase()}…`}
                emptyText={emptyText}
                disabled={disabled}
                className={cn(inputSizeCls(size), reqCueCls(required, cue))}
              />
            </FormControl>
            {hint && <FormDescription>{hint}</FormDescription>}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {reqLabel(label, required)}
          <UISelect
            value={typeof field.value === "string" ? field.value : ""}
            onValueChange={field.onChange}
            disabled={disabled}
          >
            <FormControl>
              <SelectTrigger ref={field.ref} className={cn(inputSizeCls(size), reqCueCls(required, cue))}>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.length === 0 ? (
                <SelectEmpty />
              ) : (
                options.map(([v, l, d]) => (
                  <SelectItem key={v} value={v} description={d}>{l}</SelectItem>
                ))
              )}
            </SelectContent>
          </UISelect>
          {hint && <FormDescription>{hint}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function AsyncSelectField<T extends FieldValues>({
  control, name, label, required, placeholder, search, resolveValue, initialOption, pinnedOptions, exclude, onValueMissing, onError, hint, className, disabled,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  required?: boolean;
  placeholder?: string;
  search: AsyncSearchFn;
  resolveValue?: AsyncResolveFn;
  initialOption?: SearchableOption | null;
  pinnedOptions?: SearchableOption[];
  exclude?: string[];
  onValueMissing?: () => void;
  onError?: (error: unknown) => void;
  hint?: string;
  className?: string;
  disabled?: boolean;
}) {
  const size = useFieldSize();
  const cue = useRequiredCue();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {reqLabel(label, required)}
          <FormControl>
            <AsyncSearchableSelect
              ref={field.ref}
              value={typeof field.value === "string" ? field.value : ""}
              onValueChange={field.onChange}
              search={search}
              resolveValue={resolveValue}
              initialOption={initialOption}
              pinnedOptions={pinnedOptions}
              exclude={exclude}
              onValueMissing={onValueMissing}
              onError={onError}
              placeholder={placeholder ?? "Select…"}
              searchPlaceholder={`Search ${label.toLowerCase()}…`}
              disabled={disabled}
              className={cn(inputSizeCls(size), reqCueCls(required, cue))}
            />
          </FormControl>
          {hint && <FormDescription>{hint}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function DateField<T extends FieldValues>({
  control, name, label, required, className, hint, disableFuture, defaultYear,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  required?: boolean;
  className?: string;
  hint?: string;
  disableFuture?: boolean;
  defaultYear?: number;
}) {
  const size = useFieldSize();
  const cue = useRequiredCue();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {reqLabel(label, required)}
          <div>
            <DatePicker
              ref={field.ref}
              value={typeof field.value === "string" ? field.value : ""}
              onChange={field.onChange}
              placeholder="mm/dd/yyyy"
              className={cn(inputSizeCls(size), reqCueCls(required, cue))}
              disableFuture={disableFuture}
              defaultYear={defaultYear}
            />
          </div>
          {hint && <FormDescription>{hint}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function RadioField<T extends FieldValues>({
  control, name, label, options,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  options: { value: string; label: string }[];
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <RadioGroup
            ref={field.ref}
            value={typeof field.value === "string" ? field.value : ""}
            onChange={field.onChange}
            options={options}
          />
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function SwitchField<T extends FieldValues>({
  control, name, label,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <ToggleRow label={label} checked={field.value === true} onChange={field.onChange} />
        </FormItem>
      )}
    />
  );
}

export function CheckField<T extends FieldValues>({
  control, name, label, className,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  className?: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn("flex flex-row items-center gap-2 space-y-0", className)}>
          <FormControl>
            <Checkbox checked={!!field.value} onCheckedChange={(c) => field.onChange(c === true)} />
          </FormControl>
          <FormLabel className="font-normal text-slate-700">{label}</FormLabel>
        </FormItem>
      )}
    />
  );
}

export function NumberField<T extends FieldValues>({
  control, name, label, prefix, suffix, className, min, max, step,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  prefix?: string;
  suffix?: string;
  className?: string;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <NumberControl
              value={typeof field.value === "number" ? field.value : null}
              onChange={field.onChange}
              prefix={prefix}
              suffix={suffix}
              min={min}
              max={max}
              step={step}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function NumberControl({
  value, onChange, prefix, suffix, min, max, step,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  prefix?: string;
  suffix?: string;
  min?: number;
  max?: number;
  step?: number;
}) {
  const size = useFieldSize();
  const [raw, setRaw] = useState<string>(value === null ? "" : String(value));

  useEffect(() => {
    setRaw((prev) => {
      const prevNum = prev.trim() === "" ? null : Number(prev);
      if (prevNum === value) return prev;
      return value === null ? "" : String(value);
    });
  }, [value]);

  const integerOnly = step !== undefined && Number.isInteger(step);

  return (
    <div className="flex items-stretch overflow-hidden rounded-md border border-input shadow-sm focus-within:ring-1 focus-within:ring-ring">
      {prefix && (
        <span className="flex items-center border-r border-input bg-muted px-2 text-xs text-muted-foreground">
          {prefix}
        </span>
      )}
      <Input
        type="number"
        inputMode={integerOnly ? "numeric" : "decimal"}
        step={step ?? "any"}
        min={min}
        max={max}
        value={raw}
        onChange={(e) => {
          const next = e.target.value;
          if (next === "") {
            setRaw(next);
            onChange(null);
            return;
          }
          const n = Number(next);
          if (Number.isNaN(n)) return;
          if (min !== undefined && n < min) return;
          if (max !== undefined && n > max) return;
          if (integerOnly && !Number.isInteger(n)) return;
          setRaw(next);
          onChange(n);
        }}
        className={cn("min-w-0 flex-1 rounded-none border-0 shadow-none focus-visible:ring-0", inputSizeCls(size))}
      />
      {suffix && (
        <span className="flex items-center border-l border-input bg-muted px-2 text-xs text-muted-foreground">
          {suffix}
        </span>
      )}
    </div>
  );
}

export function NumberCell({
  value, onChange, placeholder, className, min = 0, max, integer, ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  min?: number;
  max?: number;
  integer?: boolean;
  ariaLabel?: string;
}) {
  return (
    <Input
      type="number"
      inputMode={integer ? "numeric" : "decimal"}
      step={integer ? 1 : "any"}
      min={min}
      max={max}
      value={value}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={(e) => {
        const next = e.target.value;
        if (next === "") {
          onChange("");
          return;
        }
        const n = Number(next);
        if (Number.isNaN(n)) return;
        if (min !== undefined && n < min) return;
        if (max !== undefined && n > max) return;
        if (integer && !Number.isInteger(n)) return;
        onChange(next);
      }}
      className={className}
    />
  );
}

export const RadioGroup = forwardRef<HTMLButtonElement, {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}>(function RadioGroup({ value, onChange, options }, ref) {
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(v) => { if (v) onChange(v); }}
      variant="outline"
      className="grid grid-cols-1 gap-2 sm:grid-cols-2"
    >
      {options.map((o, i) => (
        <ToggleGroupItem
          ref={i === 0 ? ref : undefined}
          key={o.value}
          value={o.value}
          className="group h-auto w-full justify-start gap-3 whitespace-normal px-4 py-3 text-left text-sm font-medium text-muted-foreground hover:border-slate-400 data-[state=on]:border-primary data-[state=on]:bg-primary/5 data-[state=on]:text-foreground sm:justify-center sm:gap-2 sm:text-center"
        >
          <span className="flex size-4 shrink-0 items-center justify-center rounded-full border border-slate-300 group-data-[state=on]:border-primary sm:hidden">
            <span className="size-2 rounded-full bg-primary opacity-0 transition-opacity group-data-[state=on]:opacity-100" />
          </span>
          {o.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
});

function ToggleRow({
  label, checked, onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-md border border-input bg-background px-4 py-3 text-left text-sm font-medium text-slate-700 hover:border-slate-400">
      <span>{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}
